import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { toIsoDate } from 'src/shared/util/secondary-page-context';
import { SPREAD_ITEMS, SpreadChoice, spreadEndDate, spreadEndValidator, spreadSummary } from 'src/shared/util/transaction-dates';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinanceTypesService } from 'src/shared/services/finance-types.service';
import { PropertyTransactionsService } from 'src/shared/services/property-transactions.service';

import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

@Component({
  templateUrl: 'add-transaction.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddTransactionPage implements OnInit {

  private fb = inject(FormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private demoBlock = inject(DemoBlockService);
  private addressesQueries = inject(AddressesQueriesService);
  private financeTypes = inject(FinanceTypesService);
  private propertyTransactions = inject(PropertyTransactionsService);
  private destroyRef = inject(DestroyRef);

  readonly form = this.fb.group({
    date: [new Date().toISOString().slice(0, 10) as string | Date, Validators.required],
    spread: ['none' as SpreadChoice],
    spread_end: ['' as string | Date, spreadEndValidator],
    /** Empty means the money moved on the date. */
    transacted_on: ['' as string | Date],
    amount: [null as number | null, [Validators.required, Validators.min(0.01)]],
    transaction_type: ['Debit' as 'Debit' | 'Credit', Validators.required],
    monthly_finance_type_key: ['', Validators.required],
    address_id: ['', Validators.required],
    description: ['', Validators.required],
  });

  submitted = false;
  saving = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  categoryItems: SelectModel[] = [];
  propertyItems: SelectModel[] = [];

  readonly spreadItems = SPREAD_ITEMS;
  /** How each reading counts the spread just picked. */
  spreadSummary = '';

  readonly transactionTypes: SelectModel[] = [
    { value: 'Debit', viewValue: 'Debit (expense)' },
    { value: 'Credit', viewValue: 'Credit (income)' },
  ];

  boundSave = () => this.save();

  async ngOnInit() {
    this.rightSidePanelService.setDialogPageTitle('Add Transaction');
    // Only the layer flag gates the first paint, or the page renders the wrong
    // shell on mobile. The pickers fill in after it, from the TanStack caches.
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.loaded = true;
    const { date, spread, spread_end } = this.form.controls;
    merge(date.valueChanges, spread.valueChanges, spread_end.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        spread_end.updateValueAndValidity({ emitEvent: false });
        const start = toIsoDate(date.value);
        this.spreadSummary = start ? spreadSummary(start, spreadEndDate(start, spread.value, toIsoDate(spread_end.value))) : '';
      });
    this.financeTypes.getSelectItems()
      .then(items => { this.categoryItems = items; })
      .catch(() => { this.categoryItems = []; });
    this.addressesQueries.getAddressesOnce()
      .then(addresses => {
        this.propertyItems = addresses.map(address => ({
          value: String(address.id),
          viewValue: address['address_name'] || address.address1 || `Property #${address.id}`,
        }));
        if (this.propertyItems.length === 1 && !this.form.controls.address_id.value) {
          this.form.controls.address_id.setValue(this.propertyItems[0].value);
        }
      })
      .catch(() => { this.propertyItems = []; });
  }

  async save() {
    this.submitted = true;
    if (!this.form.valid || this.saving) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const value = this.form.getRawValue();
    this.saving = true;
    this.errorMessage = '';
    const start = toIsoDate(value.date);
    const end = spreadEndDate(start, value.spread, toIsoDate(value.spread_end));
    // Left empty, the paid day follows the date. A day picked here is the customer's, even the date itself.
    const paidOn = toIsoDate(value.transacted_on);
    try {
      await this.propertyTransactions.createFinancialTransaction({
        period_start_date: start,
        ...(end && end !== start ? { period_end_date: end } : {}),
        ...(paidOn ? { transacted_on: paidOn } : {}),
        amount: Math.round(Math.abs(Number(value.amount)) * 100),
        transaction_type: value.transaction_type,
        monthly_finance_type_key: value.monthly_finance_type_key,
        address_id: Number(value.address_id),
        description: value.description,
      });
      await this.storage.save('didChangeTransactions', true);
      this.util.showSuccess('Transaction added to your P&L');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Unable to add the transaction.';
    } finally {
      this.saving = false;
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
